/**
 * Converts a formatted date string into a Date object based on a given Moment.js format.
 *
 * @param {string} format - A Moment.js valid format string (e.g., 'YYYY-MM-DD hh:mm A').
 * @param {string} dateStr - The date string to be parsed, matching the specified format.
 * @returns {Date|undefined} - A Date object if the string matches the format, or undefined if it doesn't.
 *
 * @example
 * const date = stringAndFormatToDate('YYYY-MM-DD hh:mm A', '2025-04-08 03:30 PM');
 * console.log(date); // Output: Tue Apr 08 2025 15:30:00 GMT+0000 (UTC)
 */
export default function stringAndFormatToDate(format, dateStr) {
  // Using regular expressions to capture each part
  const regex = new RegExp(
    format
      .replace('YYYY', '(\\d{4})') // Year with 4 digits
      .replace('MM', '(\\d{1,2})') // Month with 1 or 2 digits
      .replace('M', '(\\d{1,2})')
      .replace('DD', '(\\d{1,2})') // Day with 1 or 2 digits
      .replace('D', '(\\d{1,2})')
      .replace('hh', '(\\d{1,2})') // Hour with 1 or 2 digits
      .replace('h', '(\\d{1,2})')
      .replace('mm', '(\\d{2})') // Minutes with 2 digits
      .replace('A', '(AM|PM|am|pm)') // AM/PM period
  );

  // Capture the values using the regular expression
  const matches = dateStr.match(regex);

  if (!matches) {
    return undefined;
  }

  // Mapping the matches to the corresponding components
  const components = {
    year: null,
    month: null,
    day: null,
    hours: null,
    minutes: null,
    period: null,
  };

  // Start from index 1 since index 0 is the full match string
  let matchIndex = 1;

  // Loop over the format and assign values based on regex matches
  format.split(/[^A-Za-z]/).forEach((part) => {
    switch (part) {
      case 'YYYY':
        components.year = Number(matches[matchIndex++]);
        break;
      case 'MM':
        components.month = Number(matches[matchIndex++]);
        break;
      case 'M':
        components.month = Number(matches[matchIndex++]);
        break;
      case 'DD':
        components.day = Number(matches[matchIndex++]);
        break;
      case 'D':
        components.day = Number(matches[matchIndex++]);
        break;
      case 'hh':
        components.hours = Number(matches[matchIndex++]);
        break;
      case 'h':
        components.hours = Number(matches[matchIndex++]);
        break;
      case 'mm':
        components.minutes = Number(matches[matchIndex++]);
        break;
      case 'A':
        components.period = matches[matchIndex++];
        break;
      default:
        break;
    }
  });

  // Adjust hour for 24-hour format
  let hour24 = parseInt(components.hours, 10);
  if (/pm/i.test(components.period) && hour24 < 12) {
    hour24 += 12; // For PM, add 12 hours (except for 12 PM)
  } else if (/am/i.test(components.period) && hour24 === 12) {
    hour24 = 0; // For 12 AM, adjust to 0 hours
  }

  // Return the Date object with the extracted values
  const date = new Date(
    components.year,
    components.month - 1,
    components.day,
    hour24,
    components.minutes
  );

  if (date instanceof Date && !isNaN(date)) {
    return date;
  }

  return undefined;
}
